/**
 * User Store
 * Centralized state management for user account data
 * (profile and the profile-update state)
 */

import { createStore } from 'zustand/vanilla';
import { devtools } from 'zustand/middleware';

// User profile (sourced from Ory via the TYPO3 proxy)
export interface UserProfile {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  emailVerified?: boolean;
}

interface UserState {
  // State
  user: UserProfile | null;
  isUpdating: boolean;

  // Actions
  setUser: (user: UserProfile | null) => void;
  setUpdating: (updating: boolean) => void;
  reset: () => void;

  // Computed (as functions for Zustand)
  getUserDisplayName: () => string | null;
}

const initialState = {
  user: null as UserProfile | null,
  isUpdating: false,
};

export const useUserStore = createStore<UserState>()(
  devtools(
    (set, get) => ({
      // Initial state
      ...initialState,

      // Actions
      setUser: (user) => set({ user }, false, 'setUser'),

      setUpdating: (isUpdating) => set({ isUpdating }, false, 'setUpdating'),

      reset: () => set(initialState, false, 'reset'),

      // Computed
      getUserDisplayName: () => {
        const user = get().user;
        if (!user) return null;

        const firstName = user.firstName || '';
        const lastName = user.lastName || '';

        if (firstName || lastName) {
          return `${firstName} ${lastName}`.trim();
        }

        return user.email || null;
      },
    }),
    { name: 'UserStore', enabled: !import.meta.env.PROD }
  )
);

// Export type for external use
export type { UserState };
